<template>
    <div :class="containerClass">
        <slot 
            :displayedItems="displayedItems"
            :allItems="items"
            :isExpanded="expanded"
        ></slot>
        <template v-if="hasMore">
            <div v-if="!inline" class="expandable-list__toggle-row">
                <v-btn
                    text
                    :class="toggleButtonClass"
                    :data-testid="dataTestId"
                    @click="toggle"
                >
                    {{ expanded ? showLessLabel : showMoreLabel }}
                </v-btn>
            </div>
            <v-btn
                v-else
                text
                :class="toggleButtonClass"
                :data-testid="dataTestId"
                @click="toggle"
            >
                {{ expanded ? showLessLabel : showMoreLabel }}
            </v-btn>
        </template>
    </div>
</template>

<script>
export default {
    name: 'ExpandableList',

    props: {
        /**
         * Full array of items to display.
         */
        items: {
            type: Array,
            required: true,
        },

        /**
         * Maximum number of items to show when collapsed.
         */
        limit: {
            type: Number,
            default: 3,
        },

        /**
         * Label for the expand button.
         */
        showMoreLabel: {
            type: String,
            default: 'View More',
        },

        /**
         * Label for the collapse button.
         */
        showLessLabel: {
            type: String,
            default: 'View Less',
        },

        /**
         * CSS class(es) applied to the toggle button.
         */
        toggleButtonClass: {
            type: [String, Array, Object],
            default: 'pa-0 text-decoration-underline',
        },

        /**
         * CSS class(es) applied to the root container element.
         */
        containerClass: {
            type: [String, Array, Object],
            default: '',
        },

        /**
         * When true, renders the toggle button inline (no wrapper div).
         * When false (default), wraps the button in a block-level toggle row.
         */
        inline: {
            type: Boolean,
            default: false,
        },

        /**
         * data-testid attribute for the toggle button.
         */
        dataTestId: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            expanded: false,
        };
    },

    computed: {
        /**
         * Returns the visible subset of items based on expanded state.
         */
        displayedItems() {
            return this.expanded ? this.items : this.items.slice(0, this.limit);
        },

        /**
         * Whether there are more items than the display limit.
         */
        hasMore() {
            return this.items.length > this.limit;
        },
    },

    methods: {
        /**
         * Toggles the expanded/collapsed state.
         */
        toggle() {
            this.expanded = !this.expanded;
        },

        /**
         * Resets the component to collapsed state.
         * Can be called via $refs from a parent component.
         */
        reset() {
            this.expanded = false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import './ExpandableList.scss';
</style>
